import type { DataSource, Sensor } from "../core";

export interface PresenceEvents {
  entered: void;
  left: void;
}

export enum DeviceType {
  NEXMOSPHERE_PRESENCE_XY_116_146_174 = "NEXMOSPHERE.PRESENCE_XY_116_146_174",
  NEXMOSPHERE_PRESENCE_AND_AIR_BUTTON_XY_240_241_251 = "NEXMOSPHERE.PRESENCE_AND_AIR_BUTTON_XY_240_241_251",
}

export interface DeviceTypeEventsMap {
  [DeviceType.NEXMOSPHERE_PRESENCE_XY_116_146_174]: PresenceEvents;
  [DeviceType.NEXMOSPHERE_PRESENCE_AND_AIR_BUTTON_XY_240_241_251]: PresenceEvents;
}

type PresenceSensor = Sensor<PresenceEvents>;
type PresenceEvent = 'entered' | 'left';
export type PresenceSignal = { state: PresenceEvent; code: string };

export abstract class BasePresenceSensor implements PresenceSensor {
  protected eventTarget = new EventTarget();
  private listenerMap = new Map<Function, EventListener>();
  private readonly unsubscribe: () => void;

  constructor(protected dataSource: DataSource) {
    this.unsubscribe = dataSource.onData(this.handleData.bind(this));
  }

  dispose(): void {
    this.unsubscribe();
    this.listenerMap.clear();
  }

  private handleData(data: string): void {
    const parsedData = this.parse(data);
    if (!parsedData) {
      return;
    }
    console.debug('Parsed signal:', parsedData);
    switch (parsedData.state) {
      case 'entered':
        this.eventTarget.dispatchEvent(new MessageEvent('entered', { data: { code: parsedData.code, message: 'Entered' } }));
        break;
      case 'left':
        this.eventTarget.dispatchEvent(new MessageEvent('left', { data: { code: parsedData.code, message: 'Left' } }));
        break;
    }
  }

  on<K extends keyof PresenceEvents>(event: K, callback: (payload: PresenceEvents[K]) => void): this {
    const wrapper: EventListener = (e) => callback((e as MessageEvent).data);
    this.listenerMap.set(callback, wrapper);
    this.eventTarget.addEventListener(event, wrapper);
    return this;
  }

  off<K extends keyof PresenceEvents>(event: K, callback: (payload: PresenceEvents[K]) => void): this {
    const wrapper = this.listenerMap.get(callback);
    if (wrapper) {
      this.eventTarget.removeEventListener(event, wrapper);
      this.listenerMap.delete(callback);
    }
    return this;
  }

  protected abstract parse(data: string): PresenceSignal | null;
}
